import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { CategoryDesktopFilter } from './CategoryDesktopFilter';
import * as S from './DesktopFilters.styles';
import { PriceDesktopFilter } from '@/components/Filters/DesktopFilters/PriceDesktopFilter';
import { useParseQuery } from '@/hooks/useParseQuery/useParseQuery';
import { formatNumber } from '@/utils/helpers';
import { PRICE_INPUT_PRECISION } from '@/components/Filters/FiltersDrawer.constants';
import { AdOrder } from '@/api/types/ads.types';
import { DesktopFiltersHeader } from '@/components/Filters/DesktopFilters/DesktopFiltersHeader';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { separateBooleanProperties } from '@/components/Filters/FiltersDrawer.helpers';
import {
  GetCategoryPropertiesResponse,
  PropertyType,
} from '@/api/types/categories.types';
import { CollectionDesktopFilter } from '@/components/Filters/DesktopFilters/CollectionDesktopFilter';
import {
  mapBooleanPropertyOptionsToBooleanCollection,
  mapCollectionPropertyOptionsToCollection,
  mapRangePropertyOptionsToRangeCollection,
} from '@/hooks/useParseQuery/useParseQuery.helpers';
import { useServerDataContext } from '@/contexts';
import { GET_CATEGORY_PROPERTIES } from '@/api/queries/categories.queries';
import { RangeDesktopFilter } from '@/components/Filters/DesktopFilters/RangeDesktopFilter';
import { BooleanDesktopFilter } from '@/components/Filters/DesktopFilters/BooleanDesktopFilter';
import { GetRegionsResponse } from '@/api/types/regions.types';
import { GET_REGIONS } from '@/api/queries/regions.queries';
import { RegionDesktopFilter } from '@/components/Filters/DesktopFilters/RegionDesktopFilter';
import { DESKTOP_FILTERS_CONTAINER_ID } from '@/components/Filters/DesktopFilters/DesktopFilters.constants';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';

export const DesktopFilters = () => {
  const {
    propertyOptions: {
      boolean: booleanPropertyOptions,
      range: rangePropertyOptions,
      collection: collectionPropertyOptions,
    },
    price: { from: priceFrom, to: priceTo },
    regionLegacyId,
    order,
  } = useParseQuery();
  const {
    currentCategory: serverCurrentCategory,
    categoryProperties: serverCategoryProperties,
    regions: serverRegions,
  } = useServerDataContext();
  const { currentCategory = serverCurrentCategory, isAllCategories } =
    useCurrentCategory();
  const { data = serverCategoryProperties, previousData } =
    useQuery<GetCategoryPropertiesResponse>(GET_CATEGORY_PROPERTIES, {
      variables: {
        path: currentCategory?.path,
        collectionFilters: collectionPropertyOptions,
      },
      errorPolicy: 'all',
    });
  const { data: regions = serverRegions } =
    useQuery<GetRegionsResponse>(GET_REGIONS);

  const categoryProperties = (data || previousData)?.category?.properties ?? [];
  const currentRegion =
    regions?.regions?.find(
      r => Number(r.legacyResourceId) === Number(regionLegacyId)
    ) ?? null;

  const {
    currentCollection,
    currentRangeCollection,
    currentBooleanCollection,
  } = useMemo(
    () => ({
      currentBooleanCollection: mapBooleanPropertyOptionsToBooleanCollection(
        booleanPropertyOptions
      ),
      currentRangeCollection:
        mapRangePropertyOptionsToRangeCollection(rangePropertyOptions),
      currentCollection: mapCollectionPropertyOptionsToCollection(
        collectionPropertyOptions
      ),
    }),
    [booleanPropertyOptions, rangePropertyOptions, collectionPropertyOptions]
  );

  const { booleanProperties, restProperties } =
    separateBooleanProperties(categoryProperties);

  const filtersCount = useMemo(() => {
    let result = 0;

    const conditions = [
      !isAllCategories,
      !!(priceFrom || priceTo),
      !!regionLegacyId,
      order && order !== AdOrder.DateDesc,
      ...Object.values(collectionPropertyOptions),
      ...Object.values(booleanPropertyOptions).map(el => el !== undefined),
      ...Object.values(rangePropertyOptions).map(
        val => !!(val && (val.valueFrom || val.valueTo))
      ),
    ];

    result = conditions.reduce(
      (sum, condition) => sum + (condition ? 1 : 0),
      0
    );
    return result;
  }, [
    isAllCategories,
    priceFrom,
    priceTo,
    regionLegacyId,
    order,
    collectionPropertyOptions,
    booleanPropertyOptions,
    rangePropertyOptions,
  ]);

  const prices = {
    from: priceFrom
      ? formatNumber(priceFrom, PRICE_INPUT_PRECISION, true)
      : null,
    to: priceTo ? formatNumber(priceTo, PRICE_INPUT_PRECISION, true) : null,
  };

  const isResetButtonDisabled =
    (filtersCount === 1 && !isAllCategories) || !filtersCount;

  return (
    <S.Container
      id={DESKTOP_FILTERS_CONTAINER_ID}
      data-testid={SearchResultsPageTestIds.desktopFilters.container}
    >
      <CategoryDesktopFilter />
      <S.FiltersContainer>
        <DesktopFiltersHeader
          filtersCount={filtersCount}
          isResetButtonDisabled={isResetButtonDisabled}
        />
        <PriceDesktopFilter currentPrices={prices} />
        {!!restProperties.length &&
          restProperties.map(p => {
            switch (p.type) {
              case PropertyType.CollectionProperty:
                return (
                  <CollectionDesktopFilter
                    key={p.legacyResourceId}
                    collection={currentCollection}
                    propertyLegacyId={p.legacyResourceId}
                    options={p.options}
                    popularOptions={p.popularOptions}
                    allProperties={categoryProperties}
                  />
                );
              case PropertyType.RangeProperty:
                return (
                  <RangeDesktopFilter
                    key={p.legacyResourceId}
                    property={p}
                    collection={currentRangeCollection}
                  />
                );
              default:
                return null;
            }
          })}
        {booleanProperties.map(p => (
          <BooleanDesktopFilter
            key={p.legacyResourceId}
            property={p}
            collection={currentBooleanCollection}
          />
        ))}
        <RegionDesktopFilter
          regions={regions?.regions}
          currentRegion={currentRegion}
        />
      </S.FiltersContainer>
    </S.Container>
  );
};
